import type { GooglePlacesResidenceRejectReason } from "@the-drop/types";

/**
 * Customer-facing explanation when Google Place Details says an address
 * isn't a private residence. Mirrors the copy used for the regex reject
 * messages — concise, explains the rule, and gives the user something they
 * can do about it.
 */
export function googlePlacesRejectMessage(
  reason: GooglePlacesResidenceRejectReason,
): string {
  switch (reason) {
    case "school":
      return "Colorado rules prohibit delivery to schools and school grounds. Please use a private residence.";
    case "university":
      return "Delivery is not permitted to university or college campuses. Please use a private residence.";
    case "hospital":
      return "Hospitals and medical campuses aren't eligible for delivery. Please use a private residence.";
    case "lodging":
      // Short-term rentals (Airbnb and the like) are allowed: an ordinary rental
      // is a street address to Google, and one Google lists as a cottage, guest
      // house or private room classifies as a residence (ADR-129). What lands
      // here is a hotel, motel, hostel or campground.
      return "Hotels and motels aren't eligible as delivery addresses. Please use a private residence.";
    case "post_office":
      return "Post offices and CMRAs aren't eligible. Please use a private residence.";
    case "transit_or_airport":
      return "Airports, transit, and bus/train stations aren't eligible. Please use a private residence.";
    case "business":
      return "This address is listed as a business. Delivery is allowed only to private residences.";
    case "other_establishment":
      return "This address is listed as a business or point of interest. Delivery is allowed only to private residences.";
    case "verification_unavailable":
      // Backend-side error: GOOGLE_MAPS_API_KEY missing or Google
      // unreachable. Don't expose configuration internals to the
      // customer; tell them to retry / contact support so the order
      // can't proceed without a real residence check.
      return "We can't verify this address right now. Please try again in a moment, or contact support if the problem persists.";
  }
}
